import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { CheckCircle2, Clock, XCircle, ArrowRight } from "lucide-react";

export default function DonateSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      try {
        const res = await api.get(`/donations/status/${sessionId}`);
        if (cancelled) return;
        setStatus(res.data);
        if (res.data.payment_status === "paid" || res.data.status === "expired") return;
        if (attempts++ < maxAttempts) setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setError("Could not check payment status");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const paid = status?.payment_status === "paid";
  const expired = status?.status === "expired";

  return (
    <div data-testid="donate-success-page" className="min-h-screen bg-[var(--bone)] text-[var(--ink)]">
      <Nav />
      <section className="pt-40 pb-32">
        <div className="container-x max-w-3xl">
          <div className="overline text-[var(--terracotta)]">Donation</div>
          {paid ? (
            <>
              <div className="flex items-center gap-4 mt-4">
                <CheckCircle2 className="w-12 h-12 text-[var(--terracotta)]" />
                <h1 className="font-serif text-5xl md:text-6xl tracking-[-0.025em]">Thank you.</h1>
              </div>
              <p className="text-[var(--ink)]/75 mt-6 leading-relaxed max-w-xl">
                Your gift of <strong>${status.amount} {status.currency?.toUpperCase()}</strong> is now wood, brick, wire and curiosity in the making.
                A receipt was sent to your card statement. We&apos;ll share a field report in the next quarterly email.
              </p>
              <Link to="/" data-testid="success-home-btn" className="btn-primary mt-10 inline-flex">Back home <ArrowRight className="w-4 h-4" /></Link>
            </>
          ) : expired ? (
            <>
              <div className="flex items-center gap-4 mt-4"><XCircle className="w-10 h-10" /><h1 className="font-serif text-4xl">Session expired</h1></div>
              <p className="text-[var(--ink)]/75 mt-6">The checkout session expired. Please try again.</p>
              <Link to="/donate" className="btn-primary mt-8 inline-flex">Try again <ArrowRight className="w-4 h-4" /></Link>
            </>
          ) : (
            <>
              <div className="flex items-center gap-4 mt-4"><Clock className="w-10 h-10 animate-pulse" /><h1 className="font-serif text-4xl">Confirming your donation…</h1></div>
              <p className="text-[var(--ink)]/75 mt-6">This usually takes a few seconds.</p>
              {error && <p className="text-sm text-red-700 mt-4">{error}</p>}
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
